<template>
  <div class="chart-card" ref="chartCardRef" :style="{ maxWidth: isCircularChart ? '600px' : 'auto' }">
    <div v-if="isLoading" class="loading-state">
      <div class="spinner" />
      <p>Loading chart...</p>
    </div>
    <div v-else-if="errorMessage" class="error-state">
      <p class="error-text">{{ errorMessage }}</p>
    </div>
    <div v-else-if="chartData" class="chart-wrapper">
      <div class="chart-header">
        <div class="header-left">
          <h3 v-if="chartData?.meta?.title" class="chart-title">{{ chartData.meta.title }}</h3>
        </div>
        <div class="header-right">
          <select @change="handleSelectChange" class="chart-type-select">
            <option value="line" :selected="selectedChartType === 'line'">Line</option>
            <option value="bar" :selected="selectedChartType === 'bar'">Bar</option>
            <option value="scatter" :selected="selectedChartType === 'scatter'">Scatter</option>
            <option v-if="!isMultiseries" value="pie" :selected="selectedChartType === 'pie'">Pie</option>
            <option v-if="!isMultiseries" value="doughnut" :selected="selectedChartType === 'doughnut'">Doughnut</option>
            <option value="radar" :selected="selectedChartType === 'radar'">Radar</option>
           <option disabled >─────────────</option> 
            <option value="download-png">Download PNG</option>
            <option value="fullscreen">Fullscreen</option>
          </select>
        </div>
      </div>
      <div class="chart-container" :style="{ maxWidth: isCircularChart ? '600px' : 'auto' }">
        <canvas ref="chartCanvas" class="chart-canvas" />
      </div>
    </div>
    <div v-else class="empty-state">
      <p>No chart data available</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

const props = defineProps({
  taskId: {
    type: Number,
    default: null,
  },
  groupName: {
    type: String,
    default: null,
  },
  filename: {
    type: String,
    default: null,
  },
});

const chartCanvas = ref(null);
const chartCardRef = ref(null);
const chartData = ref(null);
const isLoading = ref(false);
const errorMessage = ref('');
const selectedChartType = ref('line');
let chartInstance = null;
let isFullscreen = false;

const isMultiseries = computed(() => {
  return chartData.value?.series && chartData.value.series.length > 1;
});

const isCircularChart = computed(() => {
  return ['pie', 'doughnut', 'radar'].includes(selectedChartType.value);
});

const handleSelectChange = (event) => {
  const value = event.target.value;
  
  if (value === 'download-png') {
    downloadPNG();
    event.target.value = selectedChartType.value; // Reset to current chart type
  } else if (value === 'fullscreen') {
    toggleFullscreen();
    event.target.value = selectedChartType.value; // Reset to current chart type
  } else {
    // It's a chart type
    selectedChartType.value = value;
  }
};

const downloadPNG = async () => {
  if (!chartInstance) return;
  try {
    const originalCanvas = chartCanvas.value;
    
    // Create a new canvas with white background
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = originalCanvas.width;
    tempCanvas.height = originalCanvas.height;
    
    const tempCtx = tempCanvas.getContext('2d');
    // Fill with white background
    tempCtx.fillStyle = '#FFFFFF';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    
    // Draw the original canvas on top
    tempCtx.drawImage(originalCanvas, 0, 0);
    
    // Download the new canvas
    const link = document.createElement('a');
    link.href = tempCanvas.toDataURL('image/png');
    link.download = `${chartData.value?.meta?.title || 'chart'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('✅ PNG downloaded with white background');
  } catch (error) {
    console.error('❌ Error downloading PNG:', error);
  }
};

const toggleFullscreen = () => {
  const chartCard = chartCardRef.value;
  if (!isFullscreen) {
    // Enter fullscreen
    if (chartCard.requestFullscreen) {
      chartCard.requestFullscreen();
    } else if (chartCard.webkitRequestFullscreen) {
      chartCard.webkitRequestFullscreen();
    }
    isFullscreen = true;
  } else {
    // Exit fullscreen
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (document.webkitFullscreenElement) {
      document.webkitExitFullscreen();
    }
    isFullscreen = false;
  }
  console.log(`${isFullscreen ? '📺' : '📦'} Fullscreen toggled`);
};

const fetchChartData = async (taskId, groupName, filename) => {
  try {
    if (!taskId || !groupName || !filename) {
      return;
    }

    console.log('📥 Fetching chart data:', filename);
    isLoading.value = true;
    errorMessage.value = '';

    const dataConnect = new DatastoreConnect();
    
    const response = await dataConnect.api('task.chart.get', {
      task_id: taskId,
      group_name: groupName,
      chart_name: filename,
    });

    if (response?.result) {
      chartData.value = response.result;
      selectedChartType.value = response.result.type || 'line';
      console.log('✅ Chart data loaded:', filename);
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('❌ Error fetching chart data:', error);
    errorMessage.value = error.message || 'Failed to load chart data';
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

const generateColors = (count) => {
  const colors = [
    '#FF6384',
    '#36A2EB',
    '#FFCE56',
    '#4BC0C0',
    '#9966FF',
    '#FF9F40',
    '#FF6384',
    '#C9CBCF',
  ];
  return colors.slice(0, count);
};

const transformChartData = (data) => {
  const { type, series, meta = {} } = data;

  const colors = generateColors(series.length);
  const datasets = [];

  if (type === 'pie' || type === 'doughnut') {
    // For pie/doughnut, combine all series into one dataset
    const allLabels = series[0]?.x || [];
    const allValues = series[0]?.y || [];
    const backgroundColors = generateColors(allLabels.length);

    datasets.push({
      label: series[0]?.name || 'Data',
      data: allValues,
      backgroundColor: backgroundColors,
      borderColor: '#fff',
      borderWidth: 2,
    });

    return {
      type,
      labels: allLabels,
      datasets,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            position: 'right',
          },
          title: {
            display: !!meta.title,
            text: meta.title,
          },
        },
      },
    };
  }

  if (type === 'bubble') {
    // Bubble chart
    series.forEach((s, idx) => {
      const bubbleData = s.x.map((xVal, i) => {
        const yVal = s.y[i];
        return {
          x: xVal,
          y: Array.isArray(yVal) ? yVal[0] : yVal,
          r: Array.isArray(yVal) ? yVal[1] : 5,
        };
      });

      datasets.push({
        label: s.name,
        data: bubbleData,
        backgroundColor: colors[idx],
        borderColor: colors[idx],
        borderWidth: 1,
      });
    });

    return {
      type: 'bubble',
      labels: [],
      datasets,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          x: {
            title: {
              display: !!meta.xLabel,
              text: meta.xLabel,
            },
          },
          y: {
            title: {
              display: !!meta.yLabel,
              text: meta.yLabel,
            },
          },
        },
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: !!meta.title,
            text: meta.title,
          },
        },
      },
    };
  }

  if (type === 'scatter') {
    // Scatter chart
    const allLabels = series[0]?.x || [];

    series.forEach((s, idx) => {
      const scatterData = s.x.map((xVal, i) => ({
        x: xVal,
        y: s.y[i],
      }));

      datasets.push({
        label: s.name,
        data: scatterData,
        backgroundColor: colors[idx],
        borderColor: colors[idx],
        showLine: false,
        fill: false,
        pointRadius: 5,
        pointHoverRadius: 7,
      });
    });

    return {
      type: 'scatter',
      labels: allLabels,
      datasets,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          x: {
            title: {
              display: !!meta.xLabel,
              text: meta.xLabel,
            },
          },
          y: {
            title: {
              display: !!meta.yLabel,
              text: meta.yLabel,
            },
          },
        },
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: !!meta.title,
            text: meta.title,
          },
        },
      },
    };
  }

  if (type === 'radar') {
    // Radar chart
    const allLabels = series[0]?.x || [];

    series.forEach((s, idx) => {
      datasets.push({
        label: s.name,
        data: s.y,
        borderColor: colors[idx],
        backgroundColor: `${colors[idx]}30`,
        borderWidth: 2,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 7,
      });
    });

    return {
      type: 'radar',
      labels: allLabels,
      datasets,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            position: 'right',
          },
          title: {
            display: !!meta.title,
            text: meta.title,
          },
        },
      },
    };
  }

  if (type === 'candle') {
    // Candle (candlestick) chart - typically requires a plugin
    // We'll create a custom implementation or use standard bar as fallback
    const allLabels = series[0]?.x || [];

    series.forEach((s, idx) => {
      datasets.push({
        label: s.name,
        data: s.y,
        backgroundColor: colors[idx],
        borderColor: colors[idx],
      });
    });

    return {
      type: 'bar',
      labels: allLabels,
      datasets,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          y: {
            title: {
              display: !!meta.yLabel,
              text: meta.yLabel,
            },
          },
        },
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: !!meta.title,
            text: meta.title,
          },
        },
      },
    };
  }

  // Default: line, bar, scatter
  const allLabels = series[0]?.x || [];

  series.forEach((s, idx) => {
    datasets.push({
      label: s.name,
      data: s.y,
      backgroundColor: `${colors[idx]}30`,
      borderColor: colors[idx],
      borderWidth: 2,
      fill: type === 'line',
      tension: 0.3,
      pointRadius: 5,
      pointHoverRadius: 7,
    });
  });

  return {
    type: type || 'line',
    labels: allLabels,
    datasets,
    options: {
      responsive: true,
      maintainAspectRatio: true,
      scales: {
        x: {
          title: {
            display: !!meta.xLabel,
            text: meta.xLabel,
          },
        },
        y: {
          title: {
            display: !!meta.yLabel,
            text: meta.yLabel,
          },
        },
      },
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: !!meta.title,
          text: meta.title,
        },
      },
    },
  };
};

const renderChart = async () => {
  console.log('🎨 renderChart called');
  console.log('  chartCanvas.value:', chartCanvas.value);
  console.log('  chartData.value:', chartData.value);
  
  if (!chartCanvas.value) {
    console.error('❌ chartCanvas.value is null/undefined');
    return;
  }
  if (!chartData.value) {
    console.error('❌ chartData.value is null/undefined');
    return;
  }

  try {
    // Dynamically import Chart.js
    console.log('📦 Importing Chart.js...');
    const { Chart, registerables } = await import('chart.js');
    Chart.register(...registerables);
    console.log('✅ Chart.js imported and registered');

    // Destroy existing chart instance
    if (chartInstance) {
      chartInstance.destroy();
      console.log('🗑️ Old chart destroyed');
    }

    // Transform data
    console.log('🔄 Transforming chart data with type:', selectedChartType.value);
    const chartConfig = transformChartData(chartData.value);
    console.log('📊 Chart config:', chartConfig);

    // Create new chart
    const ctx = chartCanvas.value.getContext('2d');
    console.log('🖼️ Canvas context obtained');
    
    chartInstance = new Chart(ctx, {
      type: chartConfig.type,
      data: {
        labels: chartConfig.labels,
        datasets: chartConfig.datasets,
      },
      options: chartConfig.options,
    });
    console.log('✨ Chart created successfully!');
  } catch (error) {
    console.error('❌ Error in renderChart:', error);
  }
};

// Watch for changes to taskId, groupName, or filename
watch(
  () => [props.taskId, props.groupName, props.filename],
  ([newTaskId, newGroupName, newFilename]) => {
    if (newTaskId && newGroupName && newFilename) {
      fetchChartData(newTaskId, newGroupName, newFilename);
    }
  },
  { immediate: true }
);

// Watch for chartData changes to re-render
watch(
  () => chartData.value,
  async (newData) => {
    if (newData) {
      await renderChart();
    }
  },
  { flush: 'post' }
);

// Watch for selectedChartType changes to update chart visualization
watch(
  () => selectedChartType.value,
  async () => {
    if (chartData.value) {
      // Update chart data with new type and re-render
      chartData.value = { ...chartData.value, type: selectedChartType.value };
      await renderChart();
    }
  }
);
</script>

<style lang="scss" scoped>
.chart-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;

  @include themify() {
    background: themed('card-background');
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

.chart-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
}

.chart-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @include themify() {
    border-bottom-color: themed('border-color');
  }
}

.header-left {
  flex: 1;
}

.header-right {
  flex-shrink: 0;
}

.chart-type-select {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  background: white;
  color: #333;
  min-width: 110px;
  transition: all 0.2s ease;
  font-weight: 500;

  @include themify() {
    background: themed('input-background');
    color: themed('body-text-color');
    border-color: themed('border-color');
  }

  &:hover {
    border-color: #999;
  }

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
}

.chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;

  @include themify() {
    color: themed('body-text-color');
  }
}

.chart-container {
  flex: 1;
  position: relative;
  min-height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.chart-canvas {
  max-height: 100%;
  max-width: 100%;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;

  p {
    margin: 0;
    font-size: 14px;
    color: #666;

    @include themify() {
      color: themed('secondary-text-color');
    }
  }
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 16px;
}

.error-text {
  margin: 0;
  font-size: 14px;
  color: #d32f2f;
  text-align: center;

  @include themify() {
    color: themed('error-color');
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;

  p {
    margin: 0;
    font-size: 14px;
    color: #999;

    @include themify() {
      color: themed('secondary-text-color');
    }
  }
}
</style>
